if (typeof window === 'undefined') {

    instagramInsights(
        module.exports,
        require('./socialPosts.js'),
        require('./dimensions.js'),
        require('./aggregates.js'),
        require('./expansions.js'),
        require('../../utils/timePeriod.js'),
        require('../../../../public/enums/metrics.js'),
        require('../../../../public/modules/data/metrics.js'),
        require('lodash'),
        require('../../../../lib/riq-moment.js')
    );

} else {

    instagramInsights(
        RivalIQ.models.metrics,
        RivalIQ.models.metrics,
        RivalIQ.models.metrics,
        RivalIQ.models.metrics,
        RivalIQ.models.metrics.expansions,
        RivalIQ.utils.timePeriod,
        RivalIQ.enums,
        RivalIQ.data.metrics,
        window._,
        window.moment
    );
}

function instagramInsights(metrics, socialPostsMetrics, dimensions, aggregates, expansions, timePeriodUtils, enums, dataMetrics, _, moment) {

    'use strict';

    function getCollections() {
        return (typeof window === 'undefined' ? require('../../collections/metrics.js') : RivalIQ.collections);
    }

    function getModels() {
        return (typeof window === 'undefined' ? require('../metric.js') : RivalIQ.models);
    }

    const HOURS_A_WEEK = 168;
    const HOURS_A_DAY = 24;

    const InstagramInsightsBaseMetric = {

        omitChannelMetrics: true,

        build: function () {
            const me = this;
            const definition = socialPostsMetrics.getMetricBase().build.apply(me, arguments);
            definition.data = me.data;
            return definition;
        },

        fetch: function (attrs) {
            const me = this;

            attrs = _.extend(me.resolvedAttributes(), attrs);
            return _.result(me, 'metricsCollection')
                .fetch(attrs)
                .then(function (data) {
                    const attributes = _.extend({}, me.resolvedAttributes());
                    const models = getModels();

                    const metric = new models.Metric(attributes, data);
                    metric.unexpandedAttributes = me.unexpandedAttributes;
                    return metric;
                });
        },

        serialize: function () {
            const me = this;

            const attributesToPick = [
                'channel',
                'source',
                'currency',
                'searchTerm',
                'mediaTypeFilter',
                'tweetTypeFilter',
                'postTagIds',
                'postTagConjunction',
                'postTagExclude',
                'mustHavePostTagIds',
                'includeSummaryRow',
                'objectType',
                'storyDay',
                'orderBy',
                'sortOrder',
                'limit',
                'timezone',
                'companyId',
                'startDate',
                'endDate'
            ];

            const json = _.extend({
                concept: me.get('concept'),
                startDate: moment.utc(RivalIQ.state.view.get('timePeriod').mainPeriodStart).startOf('day').toJSON(),
                endDate: moment.utc(RivalIQ.state.view.get('timePeriod').mainPeriodEnd).endOf('day').toJSON()
            }, me.toType(),
            _.pick(me.attributes, attributesToPick));

            return json;
        },

        toType: function () {
            const me = this;
            const attrs = me.unexpandedAttributes || me.attributes;

            return _.pick(attrs, [
                'property',
                'dimensions'
            ]);
        },

        getCellView: function (data) {
            const me = this;

            return me.cellView || me.byField('cellView', data);
        },

        axisFormat: function (value, options) {
            const me = this;
            return me.byField('axisFormat', value, options) || me.format(value, options);
        },

        getIncompleteBuckets: function () {
            const me = this;

            const buckets = _.last(me.getDomainRange());
            const result = _.reduce(_.range(buckets), function (memo, idx) {
                const bucketTimePeriod = me.getBucketTimePeriod(idx);
                return (bucketTimePeriod.incomplete ? memo.concat(idx) : memo);
            }, []);

            return result;
        },

        getBucketTimePeriod: function (idx) {
            const me = this;
            let incomplete = false;
            const period = _.get(timePeriodUtils.interval, [me.get('interval')], timePeriodUtils.interval.daily);

            let bucketStartDate = me.getStartDate().add(idx, period).startOf(period);
            if (bucketStartDate < me.getStartDate()) {
                bucketStartDate = me.getStartDate();
                incomplete = true;
            }

            let bucketEndDate = me.getStartDate().add(idx, period).endOf(period);
            if (bucketEndDate > me.getEndDate()) {
                bucketEndDate = me.getEndDate();
                incomplete = true;
            }

            return {
                start: bucketStartDate,
                end: bucketEndDate,
                incomplete: incomplete,
                duration: bucketEndDate.diff(bucketStartDate, 'day', true)
            };
        },

        _convertSparseDayDataToChartValues: function (options, defaultValue) {
            const me = this;

            const buckets = _.last(me.getDomainRange());
            const values = me.getValue(_.defaults({
                format: false
            }, options));

            const isMax = me.get('property') === 'max_reach';

            const results = _.reduce(_.range(buckets), function (memo, i) {
                const bucketTimePeriod = me.getBucketTimePeriod(i);
                const bucketStartDate = bucketTimePeriod.start;
                const bucketEndDate = bucketTimePeriod.end;

                const rawBucketValues = _.filter(memo.values, function (value) {
                    const data = moment.utc(_.head(value));
                    return (data >= bucketStartDate && data <= bucketEndDate);
                });

                let bucketValue = defaultValue;
                if (_.size(rawBucketValues)) {
                    bucketValue = _.reduce(rawBucketValues, function (sum, bucketValue) {
                        return (isMax ? Math.max(sum, _.last(bucketValue)) : sum + _.last(bucketValue));
                    }, 0);
                }

                if (_.get(options, 'perDay')) {
                    bucketValue /= Math.max(bucketTimePeriod.duration, 1);
                }

                return {
                    buckets: memo.buckets.concat([[i, bucketValue]]),
                    values: _.drop(memo.values, _.size(rawBucketValues))
                };
            }, {
                values: values,
                buckets: []
            });

            return results.buckets;
        },

        hasContinuousDomain: _.constant(false)
    };

    function getFrameRetentionBuckets(metric) {
        const values = metric.clone({
            property: 'frameRetentionBucket'
        }).getValue({
            format: false
        });

        return _(values)
            .keyBy(_.head)
            .mapValues(_.last)
            .value();
    }

    function formatFrameRetentionBucketRange(valuesByKey, bucketKey) {
        const bucket = valuesByKey[bucketKey];
        // Handle case where this bucket represents a range of story day counts.
        if (bucket && bucket.min_frame_order !== bucket.max_frame_order) {
            return [bucket.min_frame_order, bucket.max_frame_order].join(' - ');
        }

        return bucketKey;
    }

    metrics.InstagramStoryDayInsights = _.extend({}, socialPostsMetrics.getMetricBase(), InstagramInsightsBaseMetric, {

        dataSource: 'instagramStoryDay',

        getSubmetricValue: function (property, submetric) {
            if (_.includes(['storyDay', 'frameRetentionBucket'], property)) {
                return submetric;
            }
        },

        metricsCollection: function () {
            return RivalIQ.apps.Insights.data.storyDay;
        },

        getDomainRange: function () {
            const me = this;

            const freeDimension = _.head(me.getFreeDimensions());
            if (freeDimension === 'frameRetention') {
                const values = me.getValue({
                    format: false
                });

                const defaultNumBuckets = 5;
                const numBuckets = Math.max(values.length, defaultNumBuckets);

                return [0, numBuckets, 1];
            } else if (freeDimension === 'storyOrder') {
                const numStories = _.maxBy(me.getValue({
                    format: false
                }), function (v) {
                    return Number(v[0]);
                });
                return [1, Number(numStories?.[0]) + 1];
            } else {
                return me.getMainPeriodDomainRange();
            }
        },

        getDomainName: function (index) {
            const me = this;
            return me.formatDomainValue(index);
        },

        formatDomainValue: function (value) {
            const me = this;

            const freeDimension = _.head(me.getFreeDimensions());
            if (freeDimension === 'frameRetention') {
                const range = formatFrameRetentionBucketRange(getFrameRetentionBuckets(me), value);
                return `${range} ${RivalIQ.formatter.numericLabel('frame', 'frames', Number(range))}`;
            } else {
                return value;
            }
        },

        getTicks: function (numTicks) {
            const me = this;

            const freeDimension = _.head(me.getFreeDimensions());
            if (freeDimension === 'frameRetention') {
                const numBuckets = me.getValue({
                    format: false
                }).length;
                return RivalIQ.models.Metric.prototype.getTicks.call(me, (numBuckets <= 9 ? numBuckets : 4));
            } else if (freeDimension === 'storyOrder') {
                const range = me.getDomainRange();

                const step = Math.floor(range[1] / numTicks);
                return _.range(1, range[1], step);
            } else {
                return RivalIQ.models.Metric.prototype.getTicks.call(me, numTicks);
            }
        },

        getTickValues: function (numTicks) {
            const me = this;

            const ticks = me.getTicks();
            const freeDimension = _.head(me.getFreeDimensions());
            if (freeDimension === 'frameRetention' || freeDimension === 'storyOrder') {
                return ticks;
            } else {
                return RivalIQ.models.Metric.prototype.getTickValues.call(me, numTicks);
            }
        },

        domainFormat: function (value, options) {
            const me = this;

            const freeDimension = _.head(me.getFreeDimensions());
            switch (freeDimension) {
                case 'storyDay':
                    if (me.get('interval')) {
                        return RivalIQ.models.Metric.prototype.domainFormat.call(me, value, options);
                    }
                    return me.getDataDate(value)[0].format('MM/DD/YYYY');

                case 'frameRetention': {
                    const valuesByKey = getFrameRetentionBuckets(me);
                    return formatFrameRetentionBucketRange(valuesByKey, me.getValue({
                        format: false
                    })[value][0]);
                }

                case 'storyOrder':
                    return value;

                default:
                    return RivalIQ.models.Metric.prototype.domainFormat.call(me, value, options);
            }
        },

        getChartData: function (options) {
            const me = this;

            const freeDimension = _.head(me.getFreeDimensions());
            if (freeDimension === 'frameRetention' || freeDimension === 'storyOrder') {
                return me.getValue(_.defaults({
                    format: false
                }, options));
            } else {
                return me._convertSparseDayDataToChartValues(options, null);
            }
        }
    });

    metrics.InstagramStoryInsights = _.extend({}, socialPostsMetrics.getMetricBase(), InstagramInsightsBaseMetric, {

        dataSource: 'instagramStory',

        getSubmetricValue: function (property, submetric) {
            if (property === 'story') {
                return submetric;
            }
        },

        metricsCollection: function () {
            return RivalIQ.apps.Insights.data.story;
        }
    });

    metrics.InstagramFollowerOnlineTimes = _.extend({}, socialPostsMetrics.getMetricBase(), InstagramInsightsBaseMetric, {
        getTimeAnalysisMode: function () {
            return enums.timeAnalysisMode.MAIN_PERIOD;
        },

        getDomainName: function (index) {
            const dayOfWeek = Math.floor(index / HOURS_A_DAY);
            const hour = index % HOURS_A_DAY;
            return moment.utc().day(dayOfWeek).hour(hour).format('dddd ha');
        },

        domainFormat: function (value) {
            if (value === 0) {
                return '';
            } else {
                return moment.utc().hour(value).format('ha');
            }
        },

        getDomainRange: function () {
            return [0, HOURS_A_WEEK];
        },

        getTicks: function (count, width) {
            const me = this;
            const models = getModels();
            if (width < 560) {
                return _.range(0, HOURS_A_WEEK, HOURS_A_DAY);
            } else if (width < 925) {
                arguments[0] = count / 2;
            }
            return models.Metric.prototype.getTicks.apply(me, arguments);
        },

        getName: function () {
            const me = this;
            const models = getModels();
            return models.Metric.prototype.getName.apply(me, arguments);
        }
    });

    const TYPE_SPECIFIC_METRICS = {
        Video: [{
            property: 'organic_video_views',
            submetrics: ['organic_video_view_rate', 'organic_video_views_per_user']
        }],
        Reel: [{
            property: 'organic_video_views',
            submetrics: ['organic_video_view_rate', 'organic_video_views_per_user']
        }],
        Carousel: [{
            property: 'total_carousel_children',
            submetrics: ['num_photo_children', 'num_video_children']
        }]
    };

    const PAID_TYPE_SPECIFIC_METRICS = {
        Video: [{
            property: 'video_views',
            submetrics: ['organic_video_views', 'paid_video_views']
        }]
    };

    function getInstagramChildProperties(options) {

        const mediaType = _.get(options, 'mediaType');

        const includePaidEngagement = _.get(options, 'includePaidEngagement', false);

        let typeSpecificMetrics = TYPE_SPECIFIC_METRICS[mediaType];
        if (includePaidEngagement) {
            typeSpecificMetrics = (PAID_TYPE_SPECIFIC_METRICS[mediaType] || typeSpecificMetrics);
        }

        const profileMetrics = [
            'profile_visits',
            {
                property: 'profile_activity_total',
                submetrics: ['profile_activity_bio_link_clicked', 'profile_activity_not_bio_link_clicked']
            },
            'follows'
        ];

        const properties = [
            (includePaidEngagement ?
                {
                    property: 'engagement_total',
                    submetrics: ['likes', 'comments', 'saves', 'shares', 'paid_link_clicks']
                } : null),
            {
                property: 'organic_engagement_total',
                submetrics: ['organic_likes', 'organic_comments', 'organic_saves', 'organic_shares']
            },
            (includePaidEngagement ?
                {
                    property: 'paid_engagement_total',
                    submetrics: ['paid_likes', 'paid_comments', 'paid_saves', 'paid_shares', 'paid_link_clicks']
                } : null),
            // NOTE carousels and reels do not have these metrics; thanks IG
            (mediaType !== 'Carousel' && mediaType !== 'Reel' ? profileMetrics : null),
            'organic_engagement_rate_by_reach',
            (includePaidEngagement ? 'paid_engagement_rate_by_reach' : null),
            (includePaidEngagement ?
                {
                    property: 'engagement_rate_by_impression',
                    submetrics: ['organic_engagement_rate_by_impression', 'paid_engagement_rate_by_impression']
                } : 'engagement_rate_by_impression'),
            (includePaidEngagement ?
                {
                    property: 'impressions',
                    submetrics: ['organic_impressions', 'paid_impressions']
                } : null),
            (includePaidEngagement ?
                {
                    property: 'organic_reach_rate',
                    submetrics: ['organic_reach', 'paid_reach']
                } : {
                    property: 'organic_reach_rate',
                    submetrics: ['organic_reach', 'organic_impressions']
                }),
            typeSpecificMetrics,
            'presence_reach'
        ];

        return properties;
    }

    function getTwitterChildProperties(options) {
        const mediaType = _.get(options, 'mediaType');
        const properties = [
            {
                property: 'engagement_total',
                submetrics: ['likes', 'replies', 'retweets', 'clicks']
            },
            {
                property: 'social_engagement_total'
            }
        ];

        if (mediaType && mediaType.toLowerCase() === 'video') {
            properties.push({
                property: 'video_views'
            });
        }

        properties.push('engagement_rate_by_impression', 'social_engagement_rate_by_impression', 'impressions', 'presence_reach');

        return properties;
    }

    function getLinkedInChildProperties(options) {
        const mediaType = (_.get(options, 'mediaType') || '').toLowerCase();
        let properties = [
            {
                property: 'engagement_total',
                submetrics: ['likes', 'comments', 'shares', 'clicks']
            },
            'social_engagement_total',
            'engagement_rate_by_impression'
        ];

        if (mediaType === 'video') {
            properties.push({
                property: 'video_views',
                submetrics: ['video_viewers', 'video_watched_minutes']
            });
        }

        properties = properties.concat([
            'impressions',
            'presence_reach'
        ]);

        return properties;
    }

    const getTiktokChildProperties = _.memoize((options) => [
        {
            property: 'engagement_total',
            submetrics: ['likes', 'comments', 'shares']
        },
        {
            property: 'video_views'
        },
        {
            property: 'engagement_rate_by_impression'
        },
        {
            property: 'impressions'
        },
        {
            property: 'presence_reach'
        }
    ]);

    const CHILD_PROPERTIES_BY_SOURCE = {
        [enums.sources.INSTAGRAM_INSIGHTS]: getInstagramChildProperties,
        [enums.sources.LINKEDIN]: getLinkedInChildProperties,
        [enums.sources.TWITTER_INSIGHTS]: getTwitterChildProperties,
        [enums.sources.TIKTOK_INSIGHTS]: getTiktokChildProperties
    };

    metrics.PostInsights = _.extend({}, socialPostsMetrics.getMetricBase(), InstagramInsightsBaseMetric, {

        dataSource: 'insightsPost',

        getSubmetricValue: function (property, submetric) {
            const me = this;

            if (property === 'post') {
                return submetric;
            }

            if (property === 'boosted_post_total_spend') {
                let value;
                const currency = me.get('currency');

                if (currency) {
                    value = _.get(submetric, ['paid_spend', currency]);
                }

                return value;
            }

            // post level post tag ids
            if (_.get(me, ['attributes', 'returns']) === 'post_tag_ids') {
                const postTagId = me.attributes.post_tag_id;
                if (submetric?.post_tag_ids) {
                    return (submetric.post_tag_ids.includes(postTagId) ? 1 : 0);
                } else {
                    return 0;
                }
            }
        },

        metricsCollection: function () {
            return RivalIQ.apps.Insights.data.post;
        },

        getChildProperties: function (options) {
            const me = this;
            const source = me.get('source');

            const getChildProperties = CHILD_PROPERTIES_BY_SOURCE[source];
            if (!getChildProperties) {
                throw new Error(`Instagram Insights - unknown source ${source}`);
            }

            const properties = _.compact(
                _.flatten(
                    getChildProperties(options)
                )
            );

            return properties;
        },

        getChildMetrics: function (options) {
            const me = this;

            const defs = me.getChildProperties(options);
            const models = getModels();
            let metrics = models.MetricDefinition.buildMetric(me, defs);

            const categories = _.get(options, 'categories');
            if (categories) {
                const collections = getCollections();
                metrics = new collections.MetricDefinitions(metrics.filter(function (metric) {
                    return _.includes(categories, metric.getCategory());
                }));
            }

            return metrics;
        },

        _canGroupByInterval: function () {
            const me = this;
            const freeDimension = _.head(me.getFreeDimensions());

            return _.includes(['postDay', 'storyDay'], freeDimension);
        },

        getDomainRange: function () {
            const me = this;

            if (me._canGroupByInterval()) {
                return me.getMainPeriodDomainRange();
            } else {
                return socialPostsMetrics.getMetricBase().getDomainRange.apply(me);
            }
        },

        getChartData: function (options) {
            const me = this;

            if (me._canGroupByInterval()) {
                return me._convertSparseDayDataToChartValues(options, 0);
            } else {
                return me.getValue(_.defaults({
                    format: false
                }, options));
            }
        },

        getDomainName: function (index, options) {
            const me = this;
            return me.domainFormat(index, options);
        },

        getTicks: function (count) {
            const me = this;

            const interval = me.get('interval');
            if (!interval || (interval && me._canGroupByInterval())) {
                return RivalIQ.models.Metric.prototype.getTicks.apply(me, arguments);
            }

            const range = me.getDomainRange();
            const rangeStart = _.head(range);
            const rangeEnd = _.last(range);
            const length = rangeEnd - rangeStart;

            if (length <= count) {
                return RivalIQ.models.Metric.prototype.getTicks.apply(me, arguments);
            }

            const values = _.range(rangeStart, rangeEnd);
            const ticks = Math.min(count || 2, length);
            const step = Math.round(_.last(_.initial(values)) / (ticks - 2));

            return [].concat(
                _.head(values),
                _.take(_.range(rangeStart + step, rangeEnd - step + 1, step), ticks - 2),
                _.last(values)
            );
        },

        postDayDomainFormat: function (value) {
            const me = this;

            let format = 'MM/DD/YYYY';
            const interval = me.get('interval');
            if (interval) {
                format = 'MM/DD';
                if (interval === timePeriodUtils.period.month) {
                    format = 'MMM';
                }
            }

            const date = moment.isMoment(value) ? value : _.head(me.getDataDate(value));
            return date.format(format);
        },

        domainFormat: function (value, options) {
            const me = this;

            if (me._canGroupByInterval()) {
                return me.postDayDomainFormat(value);
            } else {
                return socialPostsMetrics.getMetricBase().domainFormat.call(me, value, options);
            }
        }
    });
}
